import React, { useState, useEffect } from 'react';
import './InstagramPosting.css';

const InstagramPosting = ({ user }) => {
  const [instagramStatus, setInstagramStatus] = useState({
    logged_in: false,
    loading: true
  });
  const [loginForm, setLoginForm] = useState({
    username: '',
    password: '',
    loading: false
  });
  const [postForm, setPostForm] = useState({
    file: null,
    caption: '',
    type: 'video', // 'video' or 'photo'
    loading: false
  });
  const [dragActive, setDragActive] = useState(false);

  // Check Instagram login status on component mount
  useEffect(() => {
    checkInstagramStatus();
  }, [user.uid]);

  const checkInstagramStatus = async () => {
    try {
      const response = await fetch(`/api/instagram/status?userId=${user.uid}`, {
        headers: {
          'X-API-Key': localStorage.getItem('apiKey')
        }
      });
      
      const data = await response.json();
      setInstagramStatus({
        ...data,
        loading: false
      });
    } catch (error) {
      console.error('Error checking Instagram status:', error);
      setInstagramStatus({
        logged_in: false,
        loading: false,
        error: 'Failed to check status'
      });
    }
  };

  const handleInstagramLogin = async (e) => {
    e.preventDefault();
    setLoginForm({ ...loginForm, loading: true });

    try {
      const response = await fetch('/api/instagram/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': localStorage.getItem('apiKey')
        },
        body: JSON.stringify({
          username: loginForm.username,
          password: loginForm.password,
          user_id: user.uid
        })
      });

      const data = await response.json();

      if (data.success) {
        setLoginForm({
          username: '',
          password: '',
          loading: false
        });
        checkInstagramStatus(); // Refresh status
      } else {
        alert(data.error || 'Login failed');
        setLoginForm({ ...loginForm, loading: false });
      }
    } catch (error) {
      console.error('Instagram login error:', error);
      alert('Login failed. Please try again.');
      setLoginForm({ ...loginForm, loading: false });
    }
  };

  const handleInstagramLogout = async () => {
    try {
      const response = await fetch('/api/instagram/logout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': localStorage.getItem('apiKey')
        },
        body: JSON.stringify({
          user_id: user.uid
        })
      });

      const data = await response.json();

      if (data.success) {
        checkInstagramStatus(); // Refresh status
      } else {
        alert(data.error || 'Logout failed');
      }
    } catch (error) {
      console.error('Instagram logout error:', error);
      alert('Logout failed. Please try again.');
    }
  };

  const handleFileSelect = (file) => {
    if (file) {
      const fileType = file.type.startsWith('video/') ? 'video' : 'photo';
      setPostForm({
        ...postForm,
        file: file,
        type: fileType
      });
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handlePost = async (e) => {
    e.preventDefault();
    
    if (!postForm.file) {
      alert('Please select a file to post');
      return;
    }

    setPostForm({ ...postForm, loading: true });

    try {
      const formData = new FormData();
      formData.append(postForm.type, postForm.file);
      formData.append('user_id', user.uid);
      formData.append('caption', postForm.caption);

      const endpoint = `/api/instagram/post/${postForm.type}`;
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'X-API-Key': localStorage.getItem('apiKey')
        },
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        alert('Posted to Instagram successfully!');
        setPostForm({
          file: null,
          caption: '',
          type: 'video',
          loading: false
        });
      } else {
        alert(data.error || 'Failed to post to Instagram');
        setPostForm({ ...postForm, loading: false });
      }
    } catch (error) {
      console.error('Instagram post error:', error);
      alert('Failed to post to Instagram. Please try again.');
      setPostForm({ ...postForm, loading: false });
    }
  };

  if (instagramStatus.loading) {
    return (
      <div className="instagram-posting">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Checking Instagram status...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="instagram-posting">
      <div className="page-header">
        <h1>📱 Instagram Posting (Beta)</h1>
        <p>Connect your Instagram account and post directly from Twizzler</p>
        <div className="beta-badge">Beta</div>
      </div>

      {!instagramStatus.logged_in ? (
        <div className="instagram-login-section">
          <div className="login-card">
            <h2>Connect Your Instagram Account</h2>
            <p>Login with your Instagram credentials to enable posting</p>
            
            <form onSubmit={handleInstagramLogin} className="login-form">
              <div className="form-group">
                <label>Instagram Username</label>
                <input
                  type="text"
                  placeholder="your_username"
                  value={loginForm.username}
                  onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
                  required
                  disabled={loginForm.loading}
                />
              </div>
              
              <div className="form-group">
                <label>Instagram Password</label>
                <input
                  type="password"
                  placeholder="Your password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  required
                  disabled={loginForm.loading}
                />
              </div>

              <button 
                type="submit" 
                className="login-button"
                disabled={loginForm.loading}
              >
                {loginForm.loading ? (
                  <>
                    <div className="spinner small"></div>
                    Connecting...
                  </>
                ) : (
                  'Connect Instagram'
                )}
              </button>
            </form>

            <div className="login-disclaimer">
              <p><strong>⚠️ Important:</strong></p>
              <ul>
                <li>Use a secondary Instagram account for testing</li>
                <li>This uses Instagram's private API and may result in account restrictions</li>
                <li>We recommend using this feature sparingly</li>
                <li>Your credentials are securely encrypted and stored</li>
              </ul>
            </div>
          </div>
        </div>
      ) : (
        <div className="instagram-connected-section">
          <div className="status-card">
            <div className="status-header">
              <h3>✅ Instagram Connected</h3>
              <button onClick={handleInstagramLogout} className="logout-button">
                Disconnect
              </button>
            </div>
            
            <div className="account-info">
              <p><strong>Username:</strong> @{instagramStatus.username}</p>
              {instagramStatus.full_name && (
                <p><strong>Name:</strong> {instagramStatus.full_name}</p>
              )}
              {instagramStatus.follower_count !== undefined && (
                <p><strong>Followers:</strong> {instagramStatus.follower_count.toLocaleString()}</p>
              )}
              {instagramStatus.following_count !== undefined && (
                <p><strong>Following:</strong> {instagramStatus.following_count.toLocaleString()}</p>
              )}
            </div>
          </div>

          <div className="posting-section">
            <h3>📤 Create New Post</h3>
            
            <form onSubmit={handlePost} className="post-form">
              <div className="file-upload-section">
                <div 
                  className={`file-drop-zone ${dragActive ? 'active' : ''} ${postForm.file ? 'has-file' : ''}`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  {postForm.file ? (
                    <div className="file-preview">
                      {postForm.type === 'video' ? (
                        <video 
                          src={URL.createObjectURL(postForm.file)} 
                          controls 
                          className="preview-media"
                        />
                      ) : (
                        <img 
                          src={URL.createObjectURL(postForm.file)} 
                          alt="Preview" 
                          className="preview-media"
                        />
                      )}
                      <div className="file-info">
                        <p className="file-name">{postForm.file.name}</p>
                        <p className="file-type">{postForm.type.toUpperCase()}</p>
                        <button 
                          type="button" 
                          onClick={() => setPostForm({ ...postForm, file: null })}
                          className="remove-file"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="upload-icon">📁</div>
                      <p>Drag & drop your video or photo here</p>
                      <p className="upload-hint">or click to browse</p>
                      <input
                        type="file"
                        accept="video/*,image/*"
                        onChange={(e) => handleFileSelect(e.target.files[0])}
                        className="file-input"
                      />
                    </>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>Caption</label>
                <textarea
                  placeholder="Write your caption here... (optional)"
                  value={postForm.caption}
                  onChange={(e) => setPostForm({ ...postForm, caption: e.target.value })}
                  rows={4}
                  maxLength={2200}
                />
                <div className="caption-counter">
                  {postForm.caption.length}/2200
                </div>
              </div>

              <button 
                type="submit" 
                className="post-button"
                disabled={postForm.loading || !postForm.file}
              >
                {postForm.loading ? (
                  <>
                    <div className="spinner small"></div>
                    Posting to Instagram...
                  </>
                ) : (
                  `📤 Post ${postForm.type === 'video' ? 'Video' : 'Photo'} to Instagram`
                )}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default InstagramPosting; 